import { SlashForward20Regular, Alert20Regular } from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { requireSettledSession } from '@web/core/auth/password-change';
import { meQueryOptions, useLogout } from '@web/core/auth/session';
import { useSessionKeepAlive } from '@web/core/auth/use-session-keep-alive';
import { AppShell } from '@web/core/layouts/app-shell';
import { GlobalSearch } from '@web/modules/search/global-search';
import { EmbeddedShell } from '@web/core/layouts/embedded-shell';
import { apiRuntime } from '@web/core/http/api-config';
import { useVisibleNavigation } from '@web/core/navigation/use-visible-navigation';
import { PermissionsProvider, useCan } from '@web/core/permissions/permissions';
import { SettingsModal, useVisibleSettings } from '@web/core/settings/settings-modal';
import { SETTINGS_SEARCH_PARAM } from '@web/core/settings/settings-registry';
import {
  parseSettingsScreen,
  settingsScreenSearch,
  type SettingsScreenSearch,
} from '@web/core/settings/settings-screen';
import { useSettingsLink } from '@web/core/settings/use-settings-link';
import { ProfileForm } from '@web/modules/account/profile-form';
import { ChangePasswordForm } from '@web/modules/auth/change-password-form';
import { DemoExpiredDialog } from '@web/modules/auth/demo-expired-dialog';
import {
  AvailabilityControl,
  AvailabilityOptions,
  useMyPresence,
} from '@web/modules/conversations/availability/availability-control';
import { useConversationsRealtime } from '@web/modules/conversations/queries';
import { forgetThisBrowser } from '@web/modules/alerts/alert-browser';
import { DesktopAlerts } from '@web/modules/alerts/desktop-alerts';
import { NotificationPreferencesDialog } from '@web/modules/alerts/notification-preferences-dialog';
import { MyQuickRepliesDialog } from '@web/modules/conversations/quick-replies/my-quick-replies-dialog';
import { StartConversationDialog } from '@web/modules/conversations/start/start-conversation-dialog';
import { HelpSheet } from '@web/modules/help/components/help-sheet';
import { ProposalDrawer } from '@web/modules/proposals/drawer/proposal-drawer';
import { PROPOSAL_SEARCH_PARAM } from '@web/modules/proposals/drawer/use-proposal-link';
import { OnboardingHelpSection } from '@web/modules/onboarding/onboarding-help-section';
import { CORE_ONBOARDING } from '@web/modules/onboarding/onboarding-entries';
import { OnboardingProvider } from '@web/modules/onboarding/onboarding-provider';
import { SettingsTourButton } from '@web/modules/onboarding/tour/settings-tour-button';
import { useOnboardingAccountActions } from '@web/modules/onboarding/use-onboarding-account-actions';
import { CORE_NAVIGATION } from '@web/modules/navigation/navigation-entries';
import { NotificationsBell } from '@web/modules/notifications/notifications-bell';
import { useCoreSettings } from '@web/modules/settings/use-core-settings';
import { useNotificationsRealtime } from '@web/modules/timeline/queries';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@web/shared/ui/dialog';
import { toast } from '@web/shared/ui/toast';
import { useBrandColor } from '@web/core/theme/brand';
import { useIsMobile } from '@web/shared/util/use-mobile';
import { FloatingAssistant } from '@web/modules/ai/assistant/floating-assistant';

/**
 * The registries of the running system: the core entries. Plugins will merge theirs here
 * (`mergeSettings(CORE_SETTINGS, ...)`, `mergeNavigation(CORE_NAVIGATION, ...)`), the only
 * place that lists them.
 */
const NAVIGATION = CORE_NAVIGATION;
const ONBOARDING = CORE_ONBOARDING;

interface AppSearch extends SettingsScreenSearch {
  /** The settings window over the current screen: `?settings=organization.units`. */
  settings?: string;
  /** A proposal in its drawer over the current screen (spec 030): `?proposal=<id>`. */
  proposal?: string;
}

/**
 * Pathless layout: every screen of the product lives inside the application shell, and
 * needs a session. Without one (`GET /auth/me` answers 401 even after a refresh) the user
 * goes to `/login?next=<where they were going>`; with a temporary password still to replace,
 * to `/change-password?next=…` — the shell is never drawn for them (the API would refuse
 * every call anyway). Once the organization's demo period ended, every route draws only the
 * "demo ended" notice, which cannot be closed.
 */
export const Route = createFileRoute('/_app')({
  validateSearch: (search: Record<string, unknown>): AppSearch => {
    const proposal = search[PROPOSAL_SEARCH_PARAM];
    const withProposal = typeof proposal === 'string' && proposal !== '' ? { proposal } : {};
    const value = search[SETTINGS_SEARCH_PARAM];
    if (typeof value !== 'string' || value === '') return withProposal;
    // The screen of the item (`&view=new`, `&view=edit&id=…`) only exists with the window.
    return {
      ...withProposal,
      settings: value,
      ...settingsScreenSearch(parseSettingsScreen(search)),
    };
  },
  beforeLoad: async ({ context, location }) => {
    await requireSettledSession(context.queryClient, location.href);
  },
  component: AppLayout,
});

function AppLayout() {
  const { t } = useTranslation();
  const me = useQuery(meQueryOptions);
  const logout = useLogout();
  const [changingPassword, setChangingPassword] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [quickReplies, setQuickReplies] = useState(false);
  const [alertPreferences, setAlertPreferences] = useState(false);
  useSessionKeepAlive();
  useBrandColor(me.data?.membership.brandColor);

  // Cleared on sign-out or session expiry: the navigation away is already under way.
  if (!me.data) return null;
  const { user, organization, membership, demo } = me.data;

  if (demo?.expired) {
    return (
      <DemoExpiredDialog
        endsAt={demo.endsAt}
        organizationName={organization.name}
        // Inside a host, the host owns the session (ADR 0008).
        {...(apiRuntime().embedded
          ? {}
          : { onSignOut: () => logout.mutate(), signingOut: logout.isPending })}
      />
    );
  }

  return (
    <PermissionsProvider permissions={membership.permissions}>
      <OnboardingProvider registry={ONBOARDING}>
        <ShellWithSettings
          account={{
            name: user.name,
            username: user.username,
            organizationName: organization.name,
            avatarUrl: user.avatarUrl,
          }}
          // Spec 022: this browser stops receiving the member's pushes before the session ends.
          onSignOut={() => void forgetThisBrowser().finally(() => logout.mutate())}
          onChangePassword={() => setChangingPassword(true)}
          onEditProfile={() => setEditingProfile(true)}
          onOpenQuickReplies={() => setQuickReplies(true)}
          onOpenNotifications={() => setAlertPreferences(true)}
          signingOut={logout.isPending}
        />
      </OnboardingProvider>
      <MyQuickRepliesDialog open={quickReplies} onOpenChange={setQuickReplies} />
      <NotificationPreferencesDialog open={alertPreferences} onOpenChange={setAlertPreferences} />
      <Dialog open={editingProfile} onOpenChange={setEditingProfile}>
        <DialogContent closeLabel={t('common.close')}>
          <DialogHeader>
            <DialogTitle>{t('account.profile.title')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <ProfileForm
              avatarUrl={user.avatarUrl}
              name={user.name}
              username={user.username}
              email={user.email}
              onSaved={() => {
                setEditingProfile(false);
                toast.success({ title: t('account.profile.saved') });
              }}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>
      <Dialog open={changingPassword} onOpenChange={setChangingPassword}>
        <DialogContent closeLabel={t('common.close')}>
          <DialogHeader>
            <DialogTitle>{t('auth.changePassword.title')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <ChangePasswordForm
              onChanged={() => {
                setChangingPassword(false);
                toast.success({ title: t('auth.changePassword.success') });
              }}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>
    </PermissionsProvider>
  );
}

/**
 * The shell plus the settings window. Lives under the permissions provider: what the
 * session may see decides the navigation items and whether the gear and the "Settings"
 * entry show at all.
 */
function ShellWithSettings({
  onOpenQuickReplies,
  onOpenNotifications,
  ...shell
}: Omit<
  ComponentProps<typeof AppShell>,
  | 'children'
  | 'navigation'
  | 'onOpenSettings'
  | 'settingsOpen'
  | 'accountActions'
  | 'presence'
  | 'accountPanel'
  | 'mobileHeaderActions'
  | 'search'
> & { onOpenQuickReplies: () => void; onOpenNotifications: () => void }) {
  const { t } = useTranslation();
  // Personal quick replies are the person's own, not the organization's settings (spec 008,
  // decision 12): they live in the account menu.
  const canReply = useCan('core.conversation.reply');
  // Spec 015: "Nova Conversa" only for whoever may start one.
  const canStart = useCan('core.conversation.start');
  const [starting, setStarting] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const settingsRegistry = useCoreSettings();
  const visible = useVisibleSettings(settingsRegistry);
  const navigation = useVisibleNavigation(NAVIGATION);
  const { value, open } = useSettingsLink();
  const first = visible[0]?.items[0];
  const mobile = useIsMobile();
  // On a phone the availability is the ring of the avatar, changed in the account sheet.
  const presence = useMyPresence(canReply && mobile);
  // Spec 029: "Rever boas-vindas" and the switch of the tour invites.
  const onboardingActions = useOnboardingAccountActions();

  // From the menu: a phone starts at the categories (a link to an item opens the item).
  const onOpenSettings = first ? () => open(first.id, { browse: true }) : undefined;
  const onOpenHelp = () => setHelpOpen(true);
  const actions = (
    <>
      <AvailabilityControl />
      <NotificationsBell />
    </>
  );
  const accountActions = [
    // Spec 022: each member chooses the alerts that reach them on the computer.
    {
      id: 'alert-preferences',
      icon: Alert20Regular,
      label: t('alerts.preferences.menu'),
      onSelect: onOpenNotifications,
    },
    ...(canReply
      ? [
          {
            id: 'quick-replies',
            icon: SlashForward20Regular,
            label: t('quickReplies.mine.menu'),
            onSelect: onOpenQuickReplies,
          },
        ]
      : []),
    ...onboardingActions,
  ];
  const search = (
    <GlobalSearch
      navigation={navigation}
      settings={visible}
      onOpenSettings={open}
      {...(canStart ? { onStartConversation: () => setStarting(true) } : {})}
    />
  );
  const onCreate = canStart ? () => setStarting(true) : undefined;
  // Inside a host, the host draws the navigation and owns the session (ADR 0008); the rest of
  // the top bar and of the account menu is in the CRM's own bar.
  const frame = apiRuntime().embedded ? (
    <EmbeddedShell
      actions={actions}
      search={search}
      onCreate={onCreate}
      account={shell.account}
      accountActions={accountActions}
      onOpenSettings={onOpenSettings}
      onOpenHelp={onOpenHelp}
      settingsOpen={value !== undefined}
    >
      <Outlet />
    </EmbeddedShell>
  ) : (
    <AppShell
      {...shell}
      navigation={navigation}
      onOpenSettings={onOpenSettings}
      onOpenHelp={onOpenHelp}
      helpOpen={helpOpen}
      accountActions={accountActions}
      settingsOpen={value !== undefined}
      headerActions={actions}
      mobileHeaderActions={<NotificationsBell />}
      presence={presence}
      accountPanel={canReply ? <AvailabilityOptions /> : undefined}
      search={search}
      onCreate={onCreate}
    >
      <Outlet />
    </AppShell>
  );

  return (
    <>
      {frame}
      {canStart && <StartConversationDialog open={starting} onOpenChange={setStarting} />}
      <SettingsModal registry={settingsRegistry} footer={<SettingsTourButton />} />
      <ProposalDrawer />
      <HelpSheet
        open={helpOpen}
        onOpenChange={setHelpOpen}
        top={(close) => <OnboardingHelpSection onClose={close} />}
      />
      <NotificationsRealtime />
      <InboxRealtime />
      <DesktopAlerts />
      <FloatingAssistant />
    </>
  );
}

/** The bell's realtime: every signed-in member, with or without conversation permission. */
function NotificationsRealtime() {
  useNotificationsRealtime();
  return null;
}

/**
 * The inbox's realtime events, for whoever sees conversations: the badge of "Conversas", the
 * home blocks and the open screens follow them on any screen of the shell.
 */
function InboxRealtime() {
  const canView = useCan('core.conversation.view');
  return canView ? <InboxRealtimeListener /> : null;
}

function InboxRealtimeListener() {
  useConversationsRealtime();
  return null;
}
